import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { bankAccountNumber, banks } from '../party'
import { accountPaymentMethods } from './payment'

/**
 * What the company pays (บัญชีฝั่งจ่าย): its chart of accounts, bills from suppliers (ใบตั้งหนี้:
 * goods and services bought, with the supplier's tax invoice for input VAT), and payments of them
 * (ใบสำคัญจ่าย) withholding tax at source with a 50 ทวิ for the payee. Every bill is approved by a
 * manager (not who submitted it) before it is paid; money paid before the bill (an advance, จ่ายล่วงหน้า) is set
 * against it when it is. The month's journal (bills, payments, petty
 * cash, payroll) goes to the accounting firm as a spreadsheet.
 */

export const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'] as const
export type AccountType = (typeof accountTypes)[number]

/**
 * accounting.account (ผังบัญชี): an account by code. The system posts to the standard ones by
 * their codes (`postingAccounts`); the company renames them and adds its own (expenses mostly).
 */
export const account = defineResource({
  app: 'accounting',
  name: 'account',
  path: 'accounts',
  schema: z.object({
    code: z
      .string()
      .trim()
      .regex(/^\d{3,8}$/),
    name: z.string().trim().min(1).max(120),
    type: z.enum(accountTypes),
    status: z.enum(['active', 'inactive']).default('active'),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 110, priority: 1 }, form: { span: 4 } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 } },
    type: {
      filter: true,
      tones: { asset: 'info', liability: 'warning', equity: 'neutral', revenue: 'success', expense: 'progress' },
      list: { width: 130, priority: 1 },
      form: { span: 6 },
    },
    /** Posted to by the system (server-set): its code and type stay */
    system: { readOnly: true, type: 'boolean', list: { width: 100, priority: 3 } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 110, priority: 2 }, form: { span: 6 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['code', 'name', 'type', 'system', 'status'],
  defaultSort: 'code',
  importKey: 'code',
  card: { title: 'name', subtitle: 'code', status: 'type' },
})

/** A Thai SME's chart, the accounts the system posts to among them */
export const standardAccounts: { code: string; name: string; type: AccountType }[] = [
  { code: '1110', name: 'เงินสด', type: 'asset' },
  { code: '1111', name: 'เงินสดย่อย', type: 'asset' },
  { code: '1120', name: 'เงินฝากธนาคาร', type: 'asset' },
  { code: '1130', name: 'ลูกหนี้การค้า', type: 'asset' },
  { code: '1140', name: 'ภาษีซื้อ', type: 'asset' },
  { code: '1150', name: 'ภาษีเงินได้ถูกหัก ณ ที่จ่าย', type: 'asset' },
  { code: '1160', name: 'ลูกหนี้พนักงาน (เงินเบิกล่วงหน้า)', type: 'asset' },
  { code: '1170', name: 'เงินจ่ายล่วงหน้าผู้ขาย', type: 'asset' },
  { code: '2110', name: 'เจ้าหนี้การค้า', type: 'liability' },
  { code: '2120', name: 'ภาษีหัก ณ ที่จ่ายค้างจ่าย (ภ.ง.ด.1)', type: 'liability' },
  { code: '2121', name: 'ภาษีหัก ณ ที่จ่ายค้างจ่าย (ภ.ง.ด.3)', type: 'liability' },
  { code: '2122', name: 'ภาษีหัก ณ ที่จ่ายค้างจ่าย (ภ.ง.ด.53)', type: 'liability' },
  { code: '2130', name: 'เงินประกันสังคมค้างจ่าย', type: 'liability' },
  { code: '2140', name: 'เงินเดือนค้างจ่าย', type: 'liability' },
  { code: '2150', name: 'ภาษีขาย', type: 'liability' },
  { code: '2160', name: 'ค่าใช้จ่ายค้างจ่ายพนักงาน (เบิกค่าใช้จ่าย)', type: 'liability' },
  { code: '2190', name: 'เจ้าหนี้อื่น (รายการหักเงินเดือน)', type: 'liability' },
  { code: '3100', name: 'ทุนจดทะเบียน', type: 'equity' },
  { code: '4100', name: 'รายได้ค่าขนส่ง', type: 'revenue' },
  { code: '5100', name: 'เงินเดือนและค่าจ้าง', type: 'expense' },
  { code: '5110', name: 'ค่าล่วงเวลา', type: 'expense' },
  { code: '5120', name: 'ค่าเที่ยวพนักงานขับรถ', type: 'expense' },
  { code: '5130', name: 'เงินสมทบประกันสังคม (ส่วนนายจ้าง)', type: 'expense' },
  { code: '5140', name: 'ค่าชดเชยและเงินได้อื่นของพนักงาน', type: 'expense' },
  { code: '5150', name: 'สวัสดิการพนักงาน', type: 'expense' },
  { code: '5200', name: 'ค่าน้ำมันเชื้อเพลิง', type: 'expense' },
  { code: '5210', name: 'ค่าทางด่วน', type: 'expense' },
  { code: '5220', name: 'ค่าซ่อมบำรุงรถ', type: 'expense' },
  { code: '5230', name: 'ค่าวัสดุสิ้นเปลือง', type: 'expense' },
  { code: '5240', name: 'ค่าอาหารและเลี้ยงรับรอง', type: 'expense' },
  { code: '5250', name: 'ค่าไปรษณีย์และขนส่ง', type: 'expense' },
  { code: '5260', name: 'ค่าจ้างขนส่งช่วง', type: 'expense' },
  { code: '5300', name: 'ค่าเช่า', type: 'expense' },
  { code: '5310', name: 'ค่าน้ำประปาและไฟฟ้า', type: 'expense' },
  { code: '5320', name: 'ค่าโทรศัพท์และอินเทอร์เน็ต', type: 'expense' },
  { code: '5330', name: 'ค่าโฆษณา', type: 'expense' },
  { code: '5340', name: 'ค่าธรรมเนียมวิชาชีพ', type: 'expense' },
  { code: '5350', name: 'ค่าประกันภัย', type: 'expense' },
  { code: '5900', name: 'ค่าใช้จ่ายอื่น', type: 'expense' },
]

/** The accounts the journal posts to, by what they are for (codes of `standardAccounts`) */
export const postingAccounts = {
  cash: '1110',
  pettyCash: '1111',
  bank: '1120',
  inputVat: '1140',
  employeeAdvances: '1160',
  vendorAdvances: '1170',
  payables: '2110',
  whtPnd1: '2120',
  whtPnd3: '2121',
  whtPnd53: '2122',
  ssoPayable: '2130',
  salariesPayable: '2140',
  /** What employees paid for the company and claimed back (cash.claim), until it is transferred */
  employeeClaims: '2160',
  otherPayroll: '2190',
  salaries: '5100',
  overtime: '5110',
  trips: '5120',
  employerSso: '5130',
  payOther: '5140',
  otherExpense: '5900',
} as const

/** Petty cash and expense claims (cash.expense and cash.claim categories) by account */
export const pettyCashAccounts: Record<string, string> = {
  fuel: '5200',
  toll: '5210',
  repair: '5220',
  supplies: '5230',
  meal: '5240',
  postage: '5250',
  other: '5900',
}

/** Pay items (by code) by the expense account they post to; any other earning: `payOther` */
export const payItemAccounts: Record<string, string> = {
  SALARY: '5100',
  WAGE: '5100',
  ABSENT: '5100',
  UNPAID_LEAVE: '5100',
  LATE: '5100',
  OT: '5110',
  HOLIDAY: '5110',
  HOLIDAY_OT: '5110',
  TRIP: '5120',
}

/** `vat7`: the supplier charged 7% VAT on a tax invoice (claimable); `none`: no VAT (exempt, or a supplier not registered) */
export const billVatTypes = ['vat7', 'none'] as const
/** Withholding tax rates by the kind of payment: transport 1, advertising 2, services and hire of work 3, rent 5 */
export const whtRates = [0, 1, 2, 3, 5] as const
/** The income type a rate stands for on ภ.ง.ด.3/53 and the 50 ทวิ */
export const whtIncomeTypes: Record<number, string> = {
  1: 'ค่าขนส่ง',
  2: 'ค่าโฆษณา',
  3: 'ค่าบริการและค่าจ้างทำของ',
  5: 'ค่าเช่า',
}

/**
 * How the tax on a bill is paid (ผู้จ่ายเงิน on the 50 ทวิ, เงื่อนไข on ภ.ง.ด.3/53): `withheld` from what the supplier
 * gets; `always` (ออกให้ตลอดไป) the company bears all of it, so the supplier gets the whole bill (rent agreed net of
 * tax); `once` (ออกให้ครั้งเดียว) the company bears the tax on the amount once, and the tax on that is withheld
 */
export const whtConditions = ['withheld', 'always', 'once'] as const
export type WhtCondition = (typeof whtConditions)[number]

/**
 * The tax on `amount` (before VAT) at `rate` by `condition`, unrounded: the income the 50 ทวิ shows, the tax and the
 * part of it the company bears. 10,000 at 5%: withheld 10,000 / 500 / 0; always 10,526.32 / 526.32 / 526.32 (the tax
 * on the tax, for good); once 10,500 / 525 / 500
 */
export function lineWht(amount: number, rate: number, condition: WhtCondition = 'withheld') {
  const r = (Number(rate) || 0) / 100
  if (condition === 'always') {
    const tax = r < 1 ? (amount * r) / (1 - r) : 0
    return { income: amount + tax, tax, borne: tax }
  }
  if (condition === 'once') {
    const borne = amount * r
    return { income: amount + borne, tax: (amount + borne) * r, borne }
  }
  return { income: amount, tax: amount * r, borne: 0 }
}

export const billLineSchema = z.object({
  accountId: z.string().min(1),
  description: z.string().trim().min(1).max(300),
  /** Before VAT */
  amount: z.number().positive(),
  vat: z.enum(billVatTypes).default('vat7'),
  whtRate: z
    .number()
    .refine((r) => (whtRates as readonly number[]).includes(r))
    .default(0),
  /** The truck it was for (repairs, fuel), if any */
  vehicleId: z.string().nullish(),
})
export type BillLine = z.infer<typeof billLineSchema>

/** `pending`: submitted for approval; `approved`: may be paid; `partial` / `paid`: by bill payments */
export const billStatuses = ['draft', 'pending', 'approved', 'partial', 'paid', 'cancelled'] as const

/**
 * accounting.bill (ใบตั้งหนี้): something bought — goods, a repair, a service — from a supplier
 * (a partner marked as one). Its lines say what for (an expense account), VAT and the
 * withholding rate. Submitting numbers it (`AP6910-0001`) for approval; a manager who did not
 * submit it approves it (a super admin may approve their own, `approvesOwn`); then it is paid
 * by bill payments. Input VAT is claimed by the supplier's tax invoice number and date.
 */
export const bill = defineResource({
  app: 'accounting',
  name: 'bill',
  path: 'bills',
  schema: z.object({
    vendorId: z.string().min(1),
    billDate: z.iso.date(),
    dueDate: z.iso.date().nullish(),
    /** The supplier's tax invoice (or bill) number and date: the input VAT report lists them */
    vendorInvoiceNo: z.string().trim().max(50).nullish(),
    vendorInvoiceDate: z.iso.date().nullish(),
    lines: z.array(billLineSchema).min(1),
    /** How the lines' tax is paid: withheld, or borne by the company (rent it pays the tax of) */
    whtCondition: z.enum(whtConditions).default('withheld'),
    /** Photos or PDFs of the tax invoice, quotation, delivery note (file ids) */
    attachments: z.array(z.string()).default([]),
    branchId: z.string().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    vendorId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isVendor: 'true' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    billDate: { filter: true, list: { width: 130, priority: 1 }, form: { span: 3 }, copy: false },
    dueDate: { list: { width: 130, priority: 2 }, form: { span: 3 }, copy: false },
    vendorInvoiceNo: { search: true, list: { width: 140, priority: 3 }, form: { span: 6, section: 'taxInvoice' }, copy: false },
    vendorInvoiceDate: { list: { width: 130, priority: 3, hidden: true }, form: { span: 6, section: 'taxInvoice' }, copy: false },
    lines: { type: 'json', list: false, form: { span: 12, section: 'lines', custom: true }, import: false, export: false },
    whtCondition: {
      type: 'select',
      options: whtConditions,
      filter: true,
      list: { width: 140, priority: 3, hidden: true },
      form: { span: 4, section: 'lines' },
    },
    attachments: {
      type: 'json',
      list: false,
      form: { span: 12, section: 'other', custom: true },
      import: false,
      export: false,
      copy: false,
    },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch' },
      filter: true,
      list: { priority: 3, hidden: true },
      form: { span: 6, section: 'other' },
    },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
    status: {
      readOnly: true,
      type: 'select',
      options: billStatuses,
      filter: true,
      tones: { draft: 'neutral', pending: 'warning', approved: 'info', partial: 'progress', paid: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    subtotal: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    vat: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    total: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    /** Withholding tax to pay when paying, by the lines' rates (on the amounts before VAT) and `whtCondition` */
    wht: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** The part of `wht` the company bears (not taken off what the supplier gets) */
    whtBorne: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    paidAmount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    outstanding: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 2 } },
    approvedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    approvedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    /** Brought forward from the old program (server-set): its old number; not in the input VAT report or the journal */
    opening: { readOnly: true, type: 'boolean', filter: true, list: { width: 100, priority: 3, hidden: true } },
    /** Who submitted it (server-set): never its approver, unless a super admin's (`approvesOwn`) */
    submittedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
  },
  title: 'number',
  columns: [
    'number',
    'vendorId',
    'billDate',
    'dueDate',
    'total',
    'outstanding',
    'status',
    'vendorInvoiceNo',
    'subtotal',
    'vat',
    'wht',
    'branchId',
  ],
  sections: ['main', 'taxInvoice', 'lines', 'other'],
  defaultSort: '-billDate',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'approve'],
  submittable: { cancelReason: 'required' },
  // The same expense next month (rent, a phone plan): the supplier, the lines and the branch again
  duplicate: true,
  card: { title: 'vendorId', subtitle: 'number', meta: ['billDate', 'outstanding'], status: 'status' },
})

export const billAllocationSchema = z.object({
  billId: z.string().min(1),
  /** How much of the bill this payment settles (its total, VAT in, before withholding) */
  amount: z.number().positive(),
})
export type BillAllocation = z.infer<typeof billAllocationSchema>

/** How much of an advance (a payment of kind `advance`) a payment sets against what it pays out */
export const advanceUseSchema = z.object({
  paymentId: z.string().min(1),
  amount: z.number().positive(),
})
export type AdvanceUse = z.infer<typeof advanceUseSchema>

/**
 * A payment settles approved bills; an advance (จ่ายล่วงหน้า) is money paid before there is a bill — fuel for a trip
 * of a contract driver's — into the supplier's account or another they named (a driver's), set against a payment of
 * theirs later. Tax is withheld on every payment: an advance withholds at its own rate with its own 50 ทวิ, and the
 * payment it is set against withholds what is left of the bill's
 */
export const billPaymentKinds = ['payment', 'advance'] as const
export type BillPaymentKind = (typeof billPaymentKinds)[number]

/** The withholding form a payee files under: an individual ภ.ง.ด.3, a juristic person ภ.ง.ด.53 */
export const whtForms = ['pnd3', 'pnd53'] as const
export const whtFormOf = (partyType: unknown): (typeof whtForms)[number] => (partyType === 'individual' ? 'pnd3' : 'pnd53')

/**
 * `requested`: an advance dispatch asked for from a trip page, waiting for accounting to put it in the bank (a draft by
 * docstatus, set apart from accounting's own drafts); `sent`: a transfer set up in the bank, waiting for the owner to
 * approve it there (a draft with the bank's reference, `inBank`); `paid`: submitted, once the money went out
 */
export const billPaymentStatuses = ['draft', 'requested', 'sent', 'paid', 'cancelled'] as const

/**
 * Whether a draft payment is in the bank: a transfer with the reference the bank gave as it was set up there (KBiz gives
 * one before the owner approves it). Its bills are paid by nothing else meanwhile, and what it pays no longer changes
 */
export const inBank = (payment: { method?: unknown; reference?: unknown }) =>
  payment.method === 'transfer' && typeof payment.reference === 'string' && payment.reference.trim() !== ''

/**
 * accounting.billPayment (ใบสำคัญจ่าย): paying a supplier's approved bills, in part or in full —
 * by transfer, cash or cheque. Withholding tax is taken by each bill's share and paid to the
 * Revenue Department instead; submitting numbers it (`PV6910-0001`) and the 50 ทวิ certificate
 * (`WT6910-0001`), and settles the bills. Cancelling (with a reason) undoes that. An advance (`kind`) pays `amount`
 * before any bill, into `payBank…`, less `whtRate`; a payment later sets it against what it pays out (`advances`),
 * never more than is left of it (`unapplied`), withholding the bill's tax less what the advance already withheld.
 * `status` tells an advance asked for from a trip (`requested`, รอส่งธนาคาร) and a transfer set up in the bank
 * (`sent`, รอโอน) from a draft accounting saved itself.
 */
export const billPayment = defineResource({
  app: 'accounting',
  name: 'billPayment',
  path: 'bill-payments',
  schema: z.object({
    vendorId: z.string().min(1),
    paidOn: z.iso.date(),
    method: z.enum(accountPaymentMethods).default('transfer'),
    /** The bank's reference of the transfer (given as it is set up, before it is approved: `inBank`), or the cheque number and bank */
    reference: z.string().trim().max(100).nullish(),
    slipId: z.string().nullish(),
    /** Photos or PDFs of the supplier's receipt, tax invoice and the like (file ids), apart from the slip */
    attachments: z.array(z.string()).default([]),
    kind: z.enum(billPaymentKinds).default('payment'),
    /** Payments: the bills settled, at least one */
    allocations: z.array(billAllocationSchema).default([]),
    /** Payments: the supplier's advances set against what is paid out */
    advances: z.array(advanceUseSchema).default([]),
    /** Advances: the money paid, before withholding */
    amount: z.number().positive().max(10_000_000).nullish(),
    /** Advances: withheld at this rate (transport 1%): the supplier's usual one when not given */
    whtRate: z.number().min(0).max(5).nullish(),
    /** By transfer: the account paid into, one of the supplier's (`partnerAccounts`: an advance's may be a contract driver's) */
    payBank: z.enum(banks).nullish(),
    payBankAccount: bankAccountNumber,
    payBankAccountName: z.string().trim().max(120).nullish(),
    /** Advances: the trip it was asked for (tms.trip) */
    tripId: z.string().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    paidOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 3 } },
    vendorId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isVendor: 'true' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      // Warns of what the 50 ทวิ will go without (PartnerField)
      form: { span: 6, custom: true },
    },
    kind: { filter: true, tones: { payment: 'success', advance: 'info' }, list: { width: 120, priority: 2 }, form: { span: 3 } },
    method: { filter: true, list: { width: 120, priority: 2 }, optionLabels: 'accounting:payment.options.method', form: { span: 3 } },
    reference: { list: { priority: 3, hidden: true }, form: { span: 6 } },
    slipId: { type: 'file', list: false, form: { span: 6 }, import: false, export: false },
    // An advance's (the app leaves them out of a payment's form)
    // With what is withheld and paid out under it
    amount: { type: 'money', list: false, form: { span: 3, custom: true } },
    whtRate: { type: 'percent', list: false, form: { span: 3 } },
    // Set from the trip page; its number is in the notes (accounting does not open trips)
    tripId: { type: 'ref', ref: { resource: 'tms.trip' }, filter: true, list: { priority: 3, hidden: true }, form: { hidden: true } },
    // Picked from the supplier's accounts, which writes the number and name too (the app's PayToField)
    payBank: { type: 'select', options: banks, optionLabels: 'banks', list: false, form: { span: 12, section: 'payTo', custom: true } },
    payBankAccount: { list: false, form: { span: 4, section: 'payTo' }, import: false },
    payBankAccountName: { list: false, form: { span: 4, section: 'payTo' }, import: false },
    // A payment's (the app leaves them out of an advance's form)
    allocations: { type: 'json', list: false, form: { span: 12, section: 'allocations', custom: true }, import: false, export: false },
    advances: { type: 'json', list: false, form: { span: 12, section: 'allocations', custom: true }, import: false, export: false },
    attachments: { type: 'json', list: false, form: { span: 12, section: 'other', custom: true }, import: false, export: false },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
    /** Advances: the bill it is meant for, once there is one (server-set: the trip's) */
    billId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.bill' }, filter: true, list: false },
    /** Payments: the advances set against it */
    advanced: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Advances: how much later payments set against it, and what is left */
    applied: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    unapplied: { readOnly: true, type: 'money', list: { align: 'end', width: 130, priority: 3, hidden: true } },
    /** Settled on the bills: their totals' parts */
    settled: { readOnly: true, type: 'money', list: { align: 'end', priority: 2 } },
    /** Withheld: by each bill's withholding share (server-set) */
    whtAmount: { readOnly: true, type: 'money', list: { align: 'end', width: 120, priority: 2 } },
    /** Paid out: settled less what is withheld (the tax less what the company bears) and advances; an advance, its amount less withheld */
    paid: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    /** Of `whtAmount`, what the company bears: its bills' `whtCondition` */
    whtBorne: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    whtForm: { readOnly: true, type: 'select', options: whtForms, filter: true, list: { width: 110, priority: 3, hidden: true } },
    /** Its bills' (one for all: the 50 ทวิ ticks one); an advance withholds */
    whtCondition: { readOnly: true, type: 'select', options: whtConditions, filter: true, list: { width: 140, priority: 3, hidden: true } },
    /** The 50 ทวิ's number (server-set when something is withheld) */
    whtCertNo: { readOnly: true, search: true, list: { width: 130, priority: 3, hidden: true } },
    status: {
      readOnly: true,
      type: 'select',
      options: billPaymentStatuses,
      filter: true,
      tones: { draft: 'neutral', requested: 'warning', sent: 'info', paid: 'success', cancelled: 'danger' },
      list: { width: 120, priority: 1 },
    },
  },
  title: 'number',
  columns: [
    'number',
    'paidOn',
    'vendorId',
    'kind',
    'method',
    'paid',
    'status',
    'whtAmount',
    'settled',
    'unapplied',
    'whtCertNo',
    'whtForm',
  ],
  sections: ['main', 'payTo', 'allocations', 'other'],
  defaultSort: '-paidOn',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  submittable: { cancelReason: 'required' },
  card: { title: 'vendorId', subtitle: 'number', meta: ['paidOn', 'paid'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

export interface BillTotals {
  subtotal: number
  vat: number
  total: number
  /** The tax to pay to the Revenue Department */
  wht: number
  /** Of it, what the company bears (`whtCondition`) */
  whtBorne: number
  /** What is paid out in the end: total less what is withheld from the supplier */
  payable: number
}

/** A bill's totals from its lines and how their tax is paid (the form, live, and the API, stored) */
export function billTotals(
  lines: readonly Pick<BillLine, 'amount' | 'vat' | 'whtRate'>[],
  condition: WhtCondition = 'withheld',
): BillTotals {
  let subtotal = 0
  let vat = 0
  let wht = 0
  let borne = 0
  for (const l of lines) {
    const amount = Number(l.amount) || 0
    subtotal += amount
    if (l.vat === 'vat7') vat += amount * 0.07
    const tax = lineWht(amount, Number(l.whtRate) || 0, condition)
    wht += tax.tax
    borne += tax.borne
  }
  const total = round2(subtotal + vat)
  const [whtTotal, whtBorne] = [round2(wht), round2(borne)]
  return { subtotal: round2(subtotal), vat: round2(vat), total, wht: whtTotal, whtBorne, payable: round2(total - whtTotal + whtBorne) }
}

/** Withholding on paying `amount` of a bill: its share of the bill's withholding */
export function whtOnBill(bill: { total?: number | null; wht?: number | null }, amount: number): number {
  const total = Number(bill.total) || 0
  if (total <= 0) return 0
  return round2(((Number(bill.wht) || 0) * Math.min(amount, total)) / total)
}

/** The tax an advance already withheld on the part of it a payment sets off */
export function advanceWht(advance: { amount?: number | null; whtAmount?: number | null }, used: number): number {
  const amount = Number(advance.amount) || 0
  if (amount <= 0) return 0
  return round2(((Number(advance.whtAmount) || 0) * Math.min(used, amount)) / amount)
}

/**
 * A payment's totals from its allocations and the bills they settle, less the advances set against it: the tax of
 * the bills less what the advances already withheld (`wht`: `advanceWht`); what the company bears of it (the bills'
 * `whtBorne`) is not taken off what the supplier gets; then the advances off what is paid out
 */
export function billPaymentTotals(
  allocations: readonly BillAllocation[],
  bills: ReadonlyMap<string, { total?: number | null; wht?: number | null; whtBorne?: number | null }>,
  advances: readonly { amount: number; wht?: number }[] = [],
) {
  let settled = 0
  let wht = 0
  let borne = 0
  for (const a of allocations) {
    settled += a.amount
    const b = bills.get(a.billId)
    if (!b) continue
    wht += whtOnBill(b, a.amount)
    borne += whtOnBill({ total: b.total, wht: b.whtBorne }, a.amount)
  }
  const advanced = round2(advances.reduce((sum, a) => sum + (a.amount || 0), 0))
  const whtAmount = round2(Math.max(0, wht - advances.reduce((sum, a) => sum + (a.wht || 0), 0)))
  const whtBorne = round2(Math.min(borne, whtAmount))
  return { settled: round2(settled), whtAmount, whtBorne, advanced, paid: round2(settled - whtAmount + whtBorne - advanced) }
}

/** An advance's totals: its amount, withheld at its rate, the rest paid out */
export function advanceTotals(amount: number, whtRate: number) {
  const whtAmount = round2(((Number(amount) || 0) * (Number(whtRate) || 0)) / 100)
  return { settled: 0, whtAmount, whtBorne: 0, advanced: 0, paid: round2((Number(amount) || 0) - whtAmount) }
}

/** One line of the journal sent to the accounting firm */
export interface JournalLine {
  date: string
  /** The document: AP6910-0001, PV…, the pay run's name */
  document: string
  description: string
  accountCode: string
  accountName: string
  debit: number
  credit: number
}

/** Whether a set of journal lines balances (to the satang) */
export const journalBalances = (lines: readonly Pick<JournalLine, 'debit' | 'credit'>[]) =>
  Math.abs(lines.reduce((sum, l) => sum + l.debit - l.credit, 0)) < 0.005
